import React, { useCallback, useMemo } from 'react';
import SVG from 'svgjs';
import * as PropTypes from 'prop-types';
import { Spin, Row } from 'antd';
import ViaSvg, { useViaSvg } from '../../../../components/diagram/ViaSvg';
import './DeveloperDiagram.css';
import diagramAnimation from './diagram-animation';


const DIAGRAM_LOCATION = '/api/configuration/vehicle-diagrams';


/**
 * This is a test component for displaying vehicle diagrams.
 * Do not use this for anything other than reference. Use the VehicleDiagram component instead.
 *
 * @deprecated Not properly implemented. Example component only.
 * @param {string} props.diagramCode The code for loading the diagram image.
 * @param {func} props.onAreaSelect A function to execute on area selection. Areas are only selectable if this is defined.
 * @param {func} props.onAreaHover A function to execute on area hover. Areas are only hoverable if this is defined.
 * @param {string} props.className A CSS class name for the diagram html element.
 */
function DeveloperDiagram({
  diagramCode, onAreaSelect, onAreaHover, className,
}) {
  const memoAnimation = useMemo(() => ({ animation: null }), []);
  const [loading, svg] = useViaSvg(diagramCode ? `${DIAGRAM_LOCATION}/${diagramCode}` : null);

  // Callback is executed when the SVG element is attached. Wowee
  const svgRef = useCallback((svgEl) => {
    if (!svgEl) return; // SVG has failed to load for some reason.

    svgEl.setAttribute('class', `kipper-svg-root ${className}`);

    // We alway wrap the SVG with another SVG. So get the child svg here.
    const [diagramEl] = SVG.adopt(svgEl).children();

    // Initialise our animation
    memoAnimation.animation = diagramAnimation(diagramEl);

    // Bind all our event handlers.
    diagramEl.each(function onChildFound() {
      this.on('mouseover', () => {
        this.node.setAttribute('class', 'vehicle-area-highlight');
      });
      this.on('mouseout', () => {
        let mouseoutClass = this.node.getAttribute('class');
        mouseoutClass = mouseoutClass.replace('vehicle-area-highlight', '');
        this.node.setAttribute('class', mouseoutClass);
      });

      if (onAreaHover) {
        this.on('mousemove', () => {
          const { id } = this.node;
          onAreaHover(id);
        });
      }

      if (onAreaSelect) {
        this.on('click', () => {
          memoAnimation.animation.cancel();
          memoAnimation.animation.setGoalBox(this.bbox());
          memoAnimation.animation.start()
            .then(() => onAreaSelect(this.node.id));
        });
        this.on('dblclick', () => {
          memoAnimation.animation.cancel();
          memoAnimation.animation.setGoalBox(this.bbox());
          memoAnimation.animation.start(0)
            .then(() => onAreaSelect(this.node.id));
        });
      }
    });
  }, [memoAnimation.animation, onAreaHover, onAreaSelect, className]);

  return loading
    ? <Row type="flex" justify="center"><Spin /></Row>
    : <ViaSvg ref={svgRef} safeSvgHtml={svg} />;
}

DeveloperDiagram.propTypes = {
  diagramCode: PropTypes.number,
  onAreaSelect: PropTypes.func,
  onAreaHover: PropTypes.func,
  className: PropTypes.string,
};

DeveloperDiagram.defaultProps = {
  diagramCode: null,
  onAreaSelect: null,
  onAreaHover: null,
  className: '',
};

export default DeveloperDiagram;
